import * as React from "react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { IconX } from "@tabler/icons-react";
import { observer } from "mobx-react";
import {
  GuardedDialogRoot,
  usePopupChangeListeners,
  type GuardedRootProps,
} from "@/components/ui/unsaved-changes";

/** Same close rules as `Dialog` (see its JSDoc and `GuardedDialogRoot`):
 *  `dismissible={false}` refuses Escape and an outside press; a recorded change turns
 *  them into "Discard changes?" (NG-2717); `unsavedChanges` replaces detection — a sheet
 *  that saves as you go passes `false`. A non-modal sheet is unguarded by default. */
const Sheet = observer(function Sheet(props: GuardedRootProps) {
  return <GuardedDialogRoot data-slot="sheet" {...props} />;
});

const SheetTrigger = observer(function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
});

const SheetClose = observer(function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
});

const SheetPortal = observer(function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
});

const SheetOverlay = observer(function SheetOverlay({
  className,
  ...props
}: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-overlay/80 transition-opacity duration-(--motion-duration-base) ease-motion-out data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs",
        className,
      )}
      {...props}
    />
  );
});

/** The shelf of panel widths a docked sheet may pick from (NG-1176).
 *
 * Keyed by side as well as width so only the ACTIVE side's class reaches the
 * element. Emitting both left and right would be harmless — the inactive one can
 * never match its own `data-side` — but it would leave two width classes on the
 * element, and "exactly one width class here" is the invariant whose absence caused
 * this bug in the first place. Worth keeping true rather than merely harmless.
 *
 * Written out in full rather than built by interpolation: a runtime-composed class
 * like `data-[side=${side}]:sm:max-w-${width}` produces no CSS at all, because
 * Tailwind scans source text and never sees the composed name. That failure is
 * silent — the class lands and the page ignores it — which is the same family of bug
 * this prop exists to fix.
 */
const SHEET_WIDTH = {
  left: {
    sm: "data-[side=left]:sm:max-w-sm",
    md: "data-[side=left]:sm:max-w-md",
    lg: "data-[side=left]:sm:max-w-lg",
    xl: "data-[side=left]:sm:max-w-xl",
  },
  right: {
    sm: "data-[side=right]:sm:max-w-sm",
    md: "data-[side=right]:sm:max-w-md",
    lg: "data-[side=right]:sm:max-w-lg",
    xl: "data-[side=right]:sm:max-w-xl",
  },
} as const;

const SheetContent = observer(function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  closeLabel = "Close",
  overlay = true,
  width = "sm",
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  closeLabel?: string;
  /** Render the dimming backdrop. Set false for a non-modal docked panel that
   *  leaves the page behind visible (pair with `modal={false}` on `Sheet`). */
  overlay?: boolean;
  /** How wide the panel gets on `sm+` for a left/right sheet (NG-1176).
   *
   *  **A prop rather than a `className`, because a `className` could not work.**
   *  This component used to hardcode `data-[side=right]:sm:max-w-sm` in its own
   *  class list, and `cn()`/tailwind-merge does not dedupe across differing variant
   *  prefixes — so a call site's plain `sm:max-w-md` survived alongside it and then
   *  LOST on specificity. Six call sites passed a width; all six rendered at 384px.
   *  Measured: `getComputedStyle().maxWidth` came back `384px` with three `max-w`
   *  classes on the element.
   *
   *  Widths are named rather than free-form so the set stays reviewable and every
   *  sheet in the product picks from the same shelf. `top`/`bottom` sheets ignore
   *  this — they are full-width by construction. */
  width?: "sm" | "md" | "lg" | "xl";
}) {
  const { popupProps, prompt } = usePopupChangeListeners(props);
  return (
    <SheetPortal>
      {overlay ? <SheetOverlay /> : null}
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-50 flex flex-col bg-popover bg-clip-padding text-xs/relaxed text-popover-foreground shadow-lg transition duration-(--motion-duration-slow) ease-motion-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem]",
          // Applied for the docked sides only, and with the same `data-[side=...]`
          // prefix the rest of this list uses — so a call site that still passes a
          // bare `sm:max-w-*` in `className` now WINS on specificity instead of
          // silently losing to a hardcoded default (NG-1176).
          (side === "left" || side === "right") && SHEET_WIDTH[side][width],
          className,
        )}
        {...popupProps}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={<Button variant="ghost" className="absolute top-4 right-4" size="icon-sm" />}
          >
            <IconX stroke={2} />
            <span className="sr-only">{closeLabel}</span>
          </SheetPrimitive.Close>
        )}
        {prompt}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
});

const SheetHeader = observer(function SheetHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-6", className)}
      {...props}
    />
  );
});

const SheetFooter = observer(function SheetFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-6", className)}
      {...props}
    />
  );
});

const SheetTitle = observer(function SheetTitle({
  className,
  ...props
}: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-heading text-sm font-medium text-foreground", className)}
      {...props}
    />
  );
});

const SheetDescription = observer(function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-xs/relaxed text-muted-foreground", className)}
      {...props}
    />
  );
});

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
